import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import MessageBox from './MessageBox';

const UserDashboard = () => {
  const navigate = useNavigate();
  const [userItems, setUserItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [username, setUsername] = useState('');
  // const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState({
    totalItems: 0,
    lostItems: 0,
    foundItems: 0,
    claimedItems: 0
  });

  const showMessageBox = useCallback((msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
  }, []);

  const closeMessageBox = useCallback(() => {
    setMessage('');
    setMessageType('info');
  }, []);

  const checkAdminStatus = useCallback(async (user) => {
    if (!user) return false;
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.role === 'admin';
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
    return false;
  }, []);

  const fetchUserItems = useCallback(async (user) => {
    if (!user) return;
    
    try {
      // Try the optimized query first
      let userItemsQuery;
      try {
        userItemsQuery = query(
          collection(db, 'items'),
          where('userId', '==', user.uid),
          orderBy('timestamp', 'desc')
        );
        
        const snapshot = await getDocs(userItemsQuery);
        const items = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setUserItems(items);
        
        // Calculate stats
        const lostCount = items.filter(item => item.type === 'lost').length;
        const foundCount = items.filter(item => item.type === 'found').length;
        const claimedCount = items.filter(item => item.status === 'claimed').length;
        
        setStats({
          totalItems: items.length,
          lostItems: lostCount,
          foundItems: foundCount,
          claimedItems: claimedCount
        });
        
      } catch (indexError) {
        // If index error, fall back to client-side sorting
        console.log('Index not available, falling back to client-side sorting');
        
        const fallbackQuery = query(
          collection(db, 'items'),
          where('userId', '==', user.uid)
        );
        
        const snapshot = await getDocs(fallbackQuery);
        const items = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .sort((a, b) => {
          // Client-side sorting by timestamp
          const aTime = a.timestamp?.toDate?.() || new Date(a.timestamp || 0);
          const bTime = b.timestamp?.toDate?.() || new Date(b.timestamp || 0);
          return bTime - aTime;
        });
        
        setUserItems(items);
        
        // Calculate stats
        const lostCount = items.filter(item => item.type === 'lost').length;
        const foundCount = items.filter(item => item.type === 'found').length;
        const claimedCount = items.filter(item => item.status === 'claimed').length;
        
        setStats({
          totalItems: items.length,
          lostItems: lostCount,
          foundItems: foundCount,
          claimedItems: claimedCount
        });
        
        // Show helpful message about index
        if (indexError.code === 'failed-precondition' && indexError.message.includes('index')) {
          showMessageBox(
            'Database index is being created. Items loaded successfully with temporary sorting. Please wait a few minutes for full optimization.', 
            'info'
          );
        }
      }
      
    } catch (error) {
      console.error('Error loading user items:', error);
      if (error.code === 'failed-precondition' && error.message.includes('index')) {
        showMessageBox(
          'Database index is being created. Please wait a few minutes and refresh the page.', 
          'warning'
        );
      } else {
        showMessageBox('Error loading your items: ' + error.message, 'danger');
      }
    }
  }, [showMessageBox]);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      // ✅ FIXED: No need to store user in state since we only use it temporarily
      if (user) {
        const storedUsername = localStorage.getItem("username");
        setUsername(storedUsername || user.displayName || user.email);
        
        const adminStatus = await checkAdminStatus(user);
        setIsAdmin(adminStatus);
        
        await fetchUserItems(user);
      } else {
        localStorage.removeItem("username");
        navigate("/login");
      }
      setLoading(false);
    });

    return () => unsubscribeAuth();
  }, [navigate, checkAdminStatus, fetchUserItems]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getTimeSince = (timestamp) => {
    if (!timestamp) return 'Unknown';
    try {
      const now = new Date();
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return `${Math.floor(diffDays / 30)} months ago`;
    } catch {
      return 'Unknown';
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Navbar */}
      <nav className="flex items-center bg-white border-b px-6 py-3 shadow sticky top-0 z-40">
        <Link className="mr-2" to="/homepage">
          <img
            src="/Topbar-christ.jpg"
            alt="Christ University Logo"
            className="logo-christ max-w-[150px] h-auto"
          />
        </Link>
        <div className="ml-auto flex items-center gap-3">
          <span className="font-bold text-blue-900">
            Welcome: {username}
            {isAdmin && (
              <span className="ml-2 px-2 py-1 rounded-lg bg-yellow-200 text-yellow-900 text-xs font-semibold">
                Admin
              </span>
            )}
          </span>
          <ul className="flex gap-2 items-center ml-6 text-sm font-semibold">
            <li>
              <Link className="px-2 py-1 text-blue-900 hover:underline" to="/homepage">
                HOME
              </Link>
            </li>
            <li>
              <Link className="px-2 py-1 text-blue-900 hover:underline" to="/history">
                HISTORY
              </Link>
            </li>
            {isAdmin && (
              <li>
                <Link className="px-2 py-1 text-yellow-700 hover:underline" to="/admin/dashboard">
                  ADMIN PANEL
                </Link>
              </li>
            )}
            <li>
              <button
                onClick={handleLogout}
                type="button"
                className="bg-white text-center w-48 rounded-2xl h-14 relative text-black text-xl font-semibold group"
              >
                <div className="bg-red-400 rounded-xl h-12 w-1/4 flex items-center justify-center absolute left-1 top-[4px] group-hover:w-[184px] z-10 duration-500">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" height="25px" width="25px">
                    <path d="M224 480h640a32 32 0 1 1 0 64H224a32 32 0 0 1 0-64z" fill="#000000"></path>
                    <path d="m237.248 512 265.408 265.344a32 32 0 0 1-45.312 45.312l-288-288a32 32 0 0 1 0-45.312l288-288a32 32 0 1 1 45.312 45.312L237.248 512z" fill="#000000"></path>
                  </svg>
                </div>
                <p className="translate-x-2">LOGOUT</p>
              </button>
            </li>
          </ul>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto my-8 px-6">
        {message && <MessageBox message={message} type={messageType} onClose={closeMessageBox} />}

        {/* Page Header */}
        <div className="text-center mb-8">
          <h2 className="font-bold text-blue-900 text-3xl mb-2">
            <i className="bi bi-person-circle me-2"></i>
            Your Dashboard
          </h2>
          <p className="text-gray-600">Manage and track your reported lost and found items</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg border border-blue-200 p-6 text-center">
            <div className="text-blue-600 text-3xl mb-3">
              <i className="bi bi-collection"></i>
            </div>
            <h3 className="text-blue-600 text-2xl font-bold">{stats.totalItems}</h3>
            <p className="text-gray-600">Total Items</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg border border-red-200 p-6 text-center">
            <div className="text-red-600 text-3xl mb-3">
              <i className="bi bi-exclamation-triangle"></i>
            </div>
            <h3 className="text-red-600 text-2xl font-bold">{stats.lostItems}</h3>
            <p className="text-gray-600">Lost Items</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg border border-green-200 p-6 text-center">
            <div className="text-green-600 text-3xl mb-3">
              <i className="bi bi-check-circle"></i>
            </div>
            <h3 className="text-green-600 text-2xl font-bold">{stats.foundItems}</h3>
            <p className="text-gray-600">Found Items</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg border border-purple-200 p-6 text-center">
            <div className="text-purple-600 text-3xl mb-3">
              <i className="bi bi-check2-square"></i>
            </div>
            <h3 className="text-purple-600 text-2xl font-bold">{stats.claimedItems}</h3>
            <p className="text-gray-600">Claimed Items</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h5 className="font-bold text-xl mb-4 text-blue-900">
            <i className="bi bi-lightning me-2"></i>Quick Actions
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/reportitem"
              className="flex items-center justify-center bg-blue-100 text-blue-700 px-4 py-3 rounded-lg font-semibold hover:bg-blue-200 transition text-center"
            >
              <i className="bi bi-plus-circle me-2"></i>
              Report New Item
            </Link>
            <Link
              to="/all-lost-items"
              className="flex items-center justify-center bg-red-100 text-red-700 px-4 py-3 rounded-lg font-semibold hover:bg-red-200 transition text-center"
            >
              <i className="bi bi-search me-2"></i>
              Browse Lost Items
            </Link>
            <Link
              to="/all-found-items"
              className="flex items-center justify-center bg-green-100 text-green-700 px-4 py-3 rounded-lg font-semibold hover:bg-green-200 transition text-center"
            >
              <i className="bi bi-eye me-2"></i>
              Browse Found Items
            </Link>
            <Link
              to="/history"
              className="flex items-center justify-center bg-purple-100 text-purple-700 px-4 py-3 rounded-lg font-semibold hover:bg-purple-200 transition text-center"
            >
              <i className="bi bi-clock-history me-2"></i>
              View History
            </Link>
          </div>
        </div>

        {/* Your Items */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h5 className="font-bold text-xl text-blue-900">
              <i className="bi bi-list-ul me-2"></i>Your Items
            </h5>
            <Link
              to="/reportitem"
              className="bg-blue-600 text-white px-4 py-2 rounded-full font-semibold hover:bg-blue-700 transition"
            >
              <i className="bi bi-plus me-1"></i>
              Add New
            </Link>
          </div>
          
          {userItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {userItems.map((item) => (
                <div key={item.id} className="border rounded-xl p-4 shadow-sm hover:shadow-md transition">
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      className="w-full h-40 object-cover rounded-lg mb-3"
                      alt={item.itemName}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  
                  <div className="flex items-start justify-between mb-2">
                    <h6 className={`font-semibold text-lg ${
                      item.type === 'lost' ? 'text-red-700' : 'text-green-700'
                    }`}>
                      {item.itemName}
                    </h6>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      item.type === 'lost' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {item.type.toUpperCase()}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {item.description}
                  </p>
                  
                  <div className="space-y-1 text-xs text-gray-500 mb-3">
                    <div><strong>Category:</strong> {item.category}</div>
                    <div><strong>Location:</strong> {item.location}</div>
                    <div><strong>Date:</strong> {formatDate(item.reportedDate)}</div>
                    <div><strong>Posted:</strong> {getTimeSince(item.timestamp)}</div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      item.status === 'lost' ? 'bg-red-100 text-red-700' :
                      item.status === 'found' || item.status === 'unclaimed' ? 'bg-green-100 text-green-700' :
                      item.status === 'claimed' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                    </span>
                    
                    <Link
                      to={`/itemdetails/${item.id}`}
                      className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold hover:bg-blue-700 transition"
                    >
                      Manage
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">
                <i className="bi bi-inbox"></i>
              </div>
              <h6 className="text-gray-600 font-semibold mb-2">No Items Yet</h6>
              <p className="text-gray-500 mb-4">You haven't reported any lost or found items.</p>
              <Link
                to="/reportitem"
                className="bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 transition"
              >
                <i className="bi bi-plus-circle me-2"></i>
                Report Your First Item
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#000034] text-white pt-12 pb-5 mt-20 border-t border-blue-900 shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h6 className="font-bold mb-3">Quick Links</h6>
              <ul className="space-y-1 text-white/70">
                <li><Link to="/homepage" className="hover:underline">Home</Link></li>
                <li><Link to="/reportitem" className="hover:underline">Report Item</Link></li>
                <li><Link to="/history" className="hover:underline">History</Link></li>
                <li><Link to="/dashboard" className="hover:underline">Dashboard</Link></li>
              </ul>
            </div>
            <div>
              <h6 className="font-bold mb-3">Services</h6>
              <ul className="space-y-1 text-white/70">
                <li><Link to="/all-lost-items" className="hover:underline">Lost Items</Link></li>
                <li><Link to="/all-found-items" className="hover:underline">Found Items</Link></li>
                <li><span>Claims Tracking</span></li>
                <li><span>User Dashboard</span></li>
              </ul>
            </div>
            <div>
              <h6 className="font-bold mb-3">Contact</h6>
              <ul className="space-y-1 text-white/70">
                <li>Christ University, Bangalore</li>
                <li>info@christuniversity.in</li>
                <li>+91 12345 67890</li>
              </ul>
            </div>
          </div>
          <hr className="my-5 border-blue-900 opacity-40" />
          <div className="text-center">
            <small className="text-white/50">
              © 2024 Lost and Found Christ University. All rights reserved.
            </small>
          </div>
        </div>
      </footer>
    </>
  );
};

export default UserDashboard;
